import { db } from '$db/local/db';
import type { ConflictRecord } from '$db/local/db';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso } from './common';

export interface RecordConflictInput {
  entityType: string;
  entityId: string;
  localVersion: unknown;
  remoteVersion: unknown;
  localUpdatedAt: string;
  remoteUpdatedAt: string;
  resolution: 'local_won' | 'remote_won' | 'pending';
}

export async function recordConflict(
  input: RecordConflictInput
): Promise<ConflictRecord> {
  const now = nowIso();
  const record: ConflictRecord = {
    id: newId(),
    userId: getActiveUserId(),
    entityType: input.entityType,
    entityId: input.entityId,
    localPayload: (input as any).localPayload ?? null,
    remotePayload: (input as any).remotePayload ?? null,
    localVersion: Number(input.localVersion),
    remoteVersion: Number(input.remoteVersion),
    localUpdatedAt: String(input.localUpdatedAt),
    remoteUpdatedAt: input.remoteUpdatedAt,
    resolution: input.resolution,
    resolvedAt: input.resolution === 'pending' ? null : now,
    createdAt: now
  };
  await db.conflicts.add(record);
  return record;
}

export async function listConflicts(
  userId = getActiveUserId()
): Promise<ConflictRecord[]> {
  const rows = await db.conflicts.where('userId').equals(userId).toArray();
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function listPendingConflicts(
  userId = getActiveUserId()
): Promise<ConflictRecord[]> {
  const all = await listConflicts(userId);
  return all.filter((c) => c.resolution === 'pending');
}

export async function countPendingConflicts(
  userId = getActiveUserId()
): Promise<number> {
  return (await listPendingConflicts(userId)).length;
}

export async function resolveConflict(
  id: string,
  resolution: 'local_won' | 'remote_won'
): Promise<void> {
  await db.conflicts.update(id, {
    resolution,
    resolvedAt: nowIso()
  });
}

export async function deleteConflict(id: string): Promise<void> {
  await db.conflicts.delete(id);
}

export async function clearResolvedConflicts(
  userId = getActiveUserId()
): Promise<number> {
  const all = await listConflicts(userId);
  const resolved = all.filter((c) => c.resolution !== 'pending');
  if (resolved.length === 0) return 0;
  await db.conflicts.bulkDelete(resolved.map((c) => c.id));
  return resolved.length;
}